import React, { useEffect, useRef, useState } from 'react';
import { useSession } from '../App.jsx';
import { api } from '../lib/api.js';
import { ago, stamp } from '../lib/format.js';

/*
  Ledger Shield's own dashboard and reports, inside InvIT.

  The page in the frame is Ledger Shield's (web/public/ledger-shield, copied by
  web/scripts/sync-ledger-shield.mjs), so it looks and works exactly as Ledger
  Shield does. Every request it makes comes here, through window.__invitLedgerShield,
  and goes on to Ledger Shield on the client's Tally computer over the
  Connector's live line. What the frame never sees is a token: InvIT's own
  session makes each call.
*/

const LOCAL = {
  // What Ledger Shield's shell would answer itself. The pages only glance at
  // these; settings are display preferences made on Ledger Shield's own
  // settings screen, which InvIT does not carry.
  '/api/settings': () => ({ icons: {}, party_icons: true }),
  '/api/progress': () => ({ phase: 'idle' }),
  '/api/health': () => ({ ok: true }),
};

function download(file) {
  const bytes = Uint8Array.from(atob(file.content_base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
  const a = document.createElement('a');
  a.href = url; a.download = file.filename; a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export default function LedgerShieldPage({ page }) {
  const session = useSession();
  const entities = session.entities || [];
  const [entityId, setEntityId] = useState(entities[0] && entities[0].id);
  const [line, setLine] = useState(null);       // { source, offline, fetchedAt }
  const [status, setStatus] = useState(null);
  const frame = useRef(null);
  const lastExport = useRef(null);

  useEffect(() => {
    if (!entityId) return undefined;
    let live = true;
    const load = () => api.get(`/api/tally/status?entity_id=${entityId}`).then((s) => live && setStatus(s)).catch(() => {});
    load();
    const t = setInterval(load, 30_000);
    return () => { live = false; clearInterval(t); };
  }, [entityId]);

  useEffect(() => {
    // Leaving the page or switching entity ends any request still being polled,
    // so an old entity's answer never lands on the new one's frame.
    let live = true;
    const gone = () => Object.assign(new Error('The page was closed before Tally answered'), { status: 0 });
    /** One request from Ledger Shield's page. */
    window.__invitLedgerShield = async (path, { method = 'GET', body, params } = {}) => {
      if (LOCAL[path]) return LOCAL[path]();
      if (path === '/api/open') {
        // "Open" on Ledger Shield's toast: the workbook is a download here.
        if (lastExport.current) download(lastExport.current);
        return { ok: true };
      }
      const query = method === 'POST' ? (body || {}) : (params || {});
      const ask = async () => {
        let res = await api.post('/api/tally/ask', {
          entity_id: entityId, path, query,
          // A recent answer is reused unless the page asks for a refresh.
          max_age_seconds: method === 'POST' || query.refresh ? 0 : 60,
        });
        while (live && res.status === 'pending') res = await api.get(`/api/tally/requests/${res.request_id}`);
        if (!live) throw gone();
        return res;
      };
      let res;
      try {
        res = await ask();
      } catch (err) {
        if (!live) throw err;
        throw Object.assign(new Error(err.message), { hint: err.details && err.details.hint, status: err.status });
      }
      if (!live) throw gone();
      if (method === 'GET' && path !== '/api/companies') {
        setLine({ source: res.source, offline: res.status === 'offline', fetchedAt: res.fetched_at });
      }
      if (method === 'POST' && res.result && res.result.content_base64) {
        lastExport.current = res.result;
        download(res.result);
        return { file: res.result.filename, rows: res.result.rows };
      }
      return res.result;
    };
    return () => { live = false; delete window.__invitLedgerShield; };
  }, [entityId]);

  // The frame follows InvIT's theme, including a change made while it is open.
  const theme = () => document.documentElement.dataset.theme || '';
  useEffect(() => {
    const obs = new MutationObserver(() => {
      const doc = frame.current && frame.current.contentDocument;
      if (!doc) return;
      if (theme()) doc.documentElement.dataset.theme = theme(); else delete doc.documentElement.dataset.theme;
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);

  const connector = status && status.connector;
  let strip;
  if (status && !connector) strip = { kind: 'warn', text: 'No Tally computer is connected yet. Connect one from the Tally sync page.' };
  else if (line && line.offline) strip = { kind: 'warn', text: `The Tally computer is offline — showing figures from ${stamp(line.fetchedAt)}.` };
  else if (connector && !connector.online) strip = { kind: 'warn', text: `The Tally computer was last seen ${ago(connector.last_seen_at)}. Figures may be from earlier.` };
  else if (line) strip = { kind: 'ok', text: line.source === 'live' ? 'Live from Tally' : `From Tally ${ago(line.fetchedAt)}` };

  return (
    <div className="ls-host">
      <div className="ls-strip">
        {strip && <span className={`pill ${strip.kind}`}>{strip.text}</span>}
        {status && (
          <span className="muted">
            {status.entity.name} · {status.entity.tally_company
              ? <>selected in Tally: {status.entity.tally_company}</>
              : 'no company selected in Tally yet'}
          </span>
        )}
        <div className="grow" />
        {entities.length > 1 && (
          <select value={entityId} onChange={(e) => setEntityId(e.target.value)} aria-label="Entity">
            {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        )}
      </div>
      {entityId && (
        <iframe key={`${page}-${entityId}`} ref={frame} className="ls-frame" title={page === 'reports' ? 'Tally reports' : 'Tally dashboard'}
          src={`/ledger-shield/index.html?page=${page}&theme=${encodeURIComponent(theme())}`} />
      )}
    </div>
  );
}
